// JWT API 서비스 유틸리티

// API 기본 URL (통합 프록시 패턴)
// 개발/프로덕션 모두 동일한 URL 패턴 사용
// 개발: setupProxy.js에서 프록시 처리
// 프로덕션: Nginx에서 프록시 처리
const getApiBaseUrl = (endpoint = '') => {
  // /auth/ 경로가 포함된 요청은 /auth-api 사용
  const isAuthRequest = endpoint.includes('/auth/');

  if (isAuthRequest) {
    return '/auth-api/api/v1';
  } else {
    return '/board-api/api/v1';
  }
};

/**
 * JWT API 호출을 위한 유틸리티 클래스
 */
export class JWTApiService {
  constructor(apiCall) {
    this.apiCall = apiCall; // AuthContext의 apiCall 함수를 주입받음
  }

  /**
   * 로그인 API 호출
   * @param {string} userId - 사용자 ID 또는 이메일
   * @param {string} password - 비밀번호
   * @returns {Promise<Object>} 로그인 결과
   */
  async login(userId, password) {
    return await this.apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        user_id_or_email: userId,
        password: password,
      }),
    });
  }

  /**
   * 사용자 프로필 조회
   * @returns {Promise<Object>} 사용자 프로필 정보
   */
  async getUserProfile() {
    return await this.apiCall('/auth/profile');
  }

  /**
   * 게시판 목록 조회
   * @returns {Promise<Object>} 게시판 목록
   */
  async getBoards() {
    return await this.apiCall('/boards');
  }

  /**
   * 특정 게시판의 게시글 목록 조회
   * @param {number} boardId - 게시판 ID
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<Object>} 게시글 목록
   */
  async getPosts(boardId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString 
      ? `/boards/${boardId}/posts?${queryString}`
      : `/boards/${boardId}/posts`;
    
    return await this.apiCall(endpoint);
  }

  /**
   * 게시글 작성
   * @param {number} boardId - 게시판 ID
   * @param {Object} postData - 게시글 데이터
   * @returns {Promise<Object>} 작성된 게시글 정보
   */
  async createPost(boardId, postData) {
    return await this.apiCall(`/boards/${boardId}/posts`, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  /**
   * 게시글 상세 조회
   * @param {number} postId - 게시글 ID
   * @returns {Promise<Object>} 게시글 상세 정보
   */
  async getPost(postId) {
    return await this.apiCall(`/posts/${postId}`);
  }

  /**
   * 게시글 수정
   * @param {number} postId - 게시글 ID
   * @param {Object} postData - 수정할 게시글 데이터
   * @returns {Promise<Object>} 수정된 게시글 정보
   */
  async updatePost(postId, postData) {
    return await this.apiCall(`/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(postData),
    });
  }

  /**
   * 게시글 삭제
   * @param {number} postId - 게시글 ID
   * @returns {Promise<Object>} 삭제 결과
   */
  async deletePost(postId) {
    return await this.apiCall(`/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  /**
   * 댓글 목록 조회
   * @param {number} postId - 게시글 ID
   * @returns {Promise<Object>} 댓글 목록
   */
  async getComments(postId) {
    return await this.apiCall(`/posts/${postId}/comments`);
  }

  /**
   * 댓글 작성
   * @param {number} postId - 게시글 ID
   * @param {Object} commentData - 댓글 데이터
   * @returns {Promise<Object>} 작성된 댓글 정보
   */
  async createComment(postId, commentData) {
    return await this.apiCall(`/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  /**
   * 댓글 수정
   * @param {number} commentId - 댓글 ID
   * @param {Object} commentData - 수정할 댓글 데이터
   * @returns {Promise<Object>} 수정된 댓글 정보
   */
  async updateComment(commentId, commentData) {
    return await this.apiCall(`/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify(commentData),
    });
  }

  /**
   * 댓글 삭제
   * @param {number} commentId - 댓글 ID
   * @returns {Promise<Object>} 삭제 결과
   */
  async deleteComment(commentId) {
    return await this.apiCall(`/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * 게시글 조회수 증가
   * @param {number} postId - 게시글 ID
   * @returns {Promise<Object>} 조회수 증가 결과
   */
  async increasePostViews(postId) {
    return await this.apiCall(`/posts/${postId}/views`, {
      method: 'POST',
    });
  }

  /**
   * 회원가입
   * @param {Object} signupData - 회원가입 데이터
   * @returns {Promise<Object>} 회원가입 결과
   */
  async signup(signupData) {
    return await this.apiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(signupData),
    });
  }

  /**
   * 닉네임 중복 확인
   * @param {string} nickname - 확인할 닉네임
   * @returns {Promise<Object>} 중복 확인 결과
   */
  async checkNickname(nickname) {
    return await this.apiCall('/auth/check-nickname', {
      method: 'POST',
      body: JSON.stringify({ nickname }),
    });
  }

  /**
   * 프로필 수정
   * @param {Object} profileData - 수정할 프로필 데이터
   * @returns {Promise<Object>} 수정된 프로필 정보
   */
  async updateProfile(profileData) {
    return await this.apiCall('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  /**
   * 비밀번호 변경
   * @param {Object} passwordData - 비밀번호 변경 데이터
   * @returns {Promise<Object>} 변경 결과
   */
  async changePassword(passwordData) {
    return await this.apiCall('/auth/password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  }

  /**
   * 회원탈퇴
   * @returns {Promise<Object>} 탈퇴 결과
   */
  async deleteAccount() {
    return await this.apiCall('/auth/account', {
      method: 'DELETE',
    });
  }
}

/**
 * AuthContext와 연동하여 API 서비스 인스턴스를 생성하는 커스텀 훅
 * @param {Function} apiCall - AuthContext의 apiCall 함수
 * @returns {JWTApiService} API 서비스 인스턴스
 */
export const useJWTApiService = (apiCall) => {
  return new JWTApiService(apiCall);
};

/**
 * 독립적으로 사용할 수 있는 API 함수들 (JWT 토큰 없이)
 */
export const publicApiCall = async (endpoint, options = {}) => {
  try {
    const url = endpoint.startsWith('http') ? endpoint : `${getApiBaseUrl(endpoint)}${endpoint}`;

    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    console.log(`📞 Public API 호출: ${options.method || 'GET'} ${url}`);

    const response = await fetch(url, config);
    
    // 응답이 JSON인지 확인
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return data;
    } else {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return null;
    }
  } catch (error) {
    console.error('❌ Public API 호출 실패:', error);
    throw error;
  }
};

/**
 * 공개 API 함수들
 */
export const publicApi = {
  /**
   * 로그인 (토큰 없이)
   */
  login: async (userId, password) => {
    return await publicApiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        user_id_or_email: userId,
        password: password,
      }),
    });
  },

  /**
   * 회원가입 (토큰 없이)
   */
  signup: async (signupData) => {
    return await publicApiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(signupData),
    });
  },

  /**
   * 닉네임 중복 확인 (토큰 없이)
   */
  checkNickname: async (nickname) => {
    return await publicApiCall('/auth/check-nickname', {
      method: 'POST',
      body: JSON.stringify({ nickname }),
    });
  },

  /**
   * 게시판 목록 조회 (공개)
   */
  getBoards: async () => {
    return await publicApiCall('/boards');
  },

  /**
   * 게시글 목록 조회 (공개)
   */
  getPosts: async (boardId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString 
      ? `/boards/${boardId}/posts?${queryString}`
      : `/boards/${boardId}/posts`;
    
    return await publicApiCall(endpoint);
  },

  /**
   * 게시글 상세 조회 (공개)
   */
  getPost: async (postId) => {
    return await publicApiCall(`/posts/${postId}`);
  },

  /**
   * 댓글 목록 조회 (공개)
   */
  getComments: async (postId) => {
    return await publicApiCall(`/posts/${postId}/comments`);
  },
};